import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getRouteConfig, getCanonicalUrl } from '../router/routes';
import { ASSETS } from '../utils/assets';

interface UseSEOOptions {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  type?: string;
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
}

declare global {
  function gtag(...args: unknown[]): void;
}

export const useSEO = (options: UseSEOOptions = {}) => {
  const location = useLocation();
  const routeConfig = getRouteConfig(location.pathname);
  const canonicalUrl = getCanonicalUrl(location.pathname);
  
  const siteOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://stimm.digital';

  // Merge route config with custom options
  const title = options.title || routeConfig?.title || 'StimmDigital';
  const description = options.description || routeConfig?.description || '';
  const keywords = options.keywords || routeConfig?.keywords || '';
  const image = options.image || `${siteOrigin}${ASSETS.logo3DRender}`;
  const type = options.type || 'website';
  const fullUrl = `${siteOrigin}${canonicalUrl}`;

  // Schema.org structured data
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "StimmDigital",
    "url": "https://stimm.digital",
    "logo": `${siteOrigin}${ASSETS.logo}`,
    "image": `${siteOrigin}${ASSETS.logo}`,
    "priceRange": "€€",
    "sameAs": [
      "https://github.com/stimmdev",
      "https://linkedin.com/company/stimmdev"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+49-30-42141523",
      "contactType": "customer service",
      "email": "info@stimm.digital",
      "areaServed": "DE",
      "availableLanguage": ["German", "English"]
    },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Berlin",
      "addressCountry": "DE",
      "addressRegion": "Berlin"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5.0",
      "reviewCount": "47",
      "bestRating": "5",
      "worstRating": "1"
    }
  };

  // Website schema
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "StimmDigital",
    "url": "https://stimm.digital",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://stimm.digital/suche?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  // Service schema für bessere Rich Snippets
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "App- und Webentwicklung",
    "description": "Professionelle Entwicklung von Mobile Apps und Webanwendungen",
    "provider": {
      "@type": "LocalBusiness",
      "name": "StimmDigital",
      "url": "https://stimm.digital"
    },
    "areaServed": "Berlin, Deutschland",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Entwicklungsdienstleistungen",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Mobile App Entwicklung",
            "description": "iOS und Android Apps"
          },
          "priceRange": "€€€"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service", 
            "name": "Webentwicklung mit React",
            "description": "Moderne Webanwendungen"
          },
          "priceRange": "€€€"
        }
      ]
    }
  };

  useEffect(() => {
    // Analytics tracking
    if (typeof gtag !== 'undefined') {
      gtag('config', 'GA_MEASUREMENT_ID', {
        page_title: title,
        page_location: fullUrl
      });
    }
  }, [title, fullUrl]);

  return {
    title,
    description,
    keywords,
    image,
    type,
    fullUrl,
    routeConfig,
    organizationSchema,
    websiteSchema,
    serviceSchema
  };
};
